import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Attribute, AttributeValue } from "@/lib/catalog/types";
import { attributeScalarToOptionString } from "@/lib/catalog/attributeScalarString";

function sortByOrder<T extends { sortOrder?: number }>(items: T[]) {
  return [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

type ChecklistAttributeFieldProps = {
  attribute: Attribute;
  value: AttributeValue | undefined;
  onChange: (v: AttributeValue) => void;
};

export function ChecklistAttributeField({
  attribute,
  value,
  onChange,
}: ChecklistAttributeFieldProps) {
  const label = (
    <div className="flex items-center justify-between gap-3 mb-1.5">
      <Label className="text-xs text-muted-foreground font-semibold">
        {attribute.name}
        {attribute.isRequired ? <span className="text-destructive"> *</span> : null}
      </Label>
    </div>
  );

  if (attribute.type === "textarea") {
    return (
      <div className="space-y-1">
        {label}
        <Textarea
          className="glass-input min-h-[72px] text-sm"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Enter ${attribute.name.toLowerCase()}`}
        />
      </div>
    );
  }

  if (attribute.type === "boolean") {
    const checked = Boolean(value);
    return (
      <div className="space-y-1">
        {label}
        <button
          type="button"
          className={cn(
            "flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors h-10",
            checked ? "border-primary/40 bg-primary/10" : "border-border/50 bg-muted/10",
          )}
          onClick={() => onChange(!checked)}
        >
          <span className="text-muted-foreground">{checked ? "Yes" : "No"}</span>
          {checked ? <Check className="h-4 w-4 text-primary" /> : null}
        </button>
      </div>
    );
  }

  if (attribute.type === "select") {
    const current = attributeScalarToOptionString(value);
    return (
      <div className="space-y-1">
        {label}
        <Select value={current || undefined} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className="glass-input h-10 text-sm">
            <SelectValue placeholder={`Select ${attribute.name.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {sortByOrder(attribute.options ?? []).map((opt) => {
              const optValue = String(opt.value ?? "");
              if (!optValue) return null;
              return (
                <SelectItem key={opt.id} value={optValue}>
                  {opt.label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (attribute.type === "multiselect") {
    const selected = Array.isArray(value)
      ? (value as unknown[]).map((v) => attributeScalarToOptionString(v as AttributeValue))
      : [];
    const options = sortByOrder(attribute.options ?? []);
    return (
      <div className="space-y-1">
        {label}
        <div className="grid grid-cols-2 gap-2">
          {options.map((opt) => {
            const key = String(opt.value ?? "");
            if (!key) return null;
            const checked = selected.includes(key);
            return (
              <button
                type="button"
                key={opt.id}
                className={cn(
                  "rounded-lg border px-2 py-1.5 text-left text-xs transition-colors truncate",
                  checked ? "border-primary/40 bg-primary/10 text-foreground" : "border-border/50 bg-muted/10 text-muted-foreground",
                )}
                onClick={() => {
                  const next = checked ? selected.filter((v) => v !== key) : [...selected, key];
                  onChange(next);
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (attribute.type === "date") {
    return (
      <div className="space-y-1">
        {label}
        <Input
          type="date"
          className="glass-input h-10 text-sm"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (attribute.type === "number") {
    return (
      <div className="space-y-1">
        {label}
        <Input
          type="number"
          className="glass-input h-10 text-sm"
          value={typeof value === "number" ? String(value) : typeof value === "string" ? value : ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return onChange(null);
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : null);
          }}
          placeholder={`Enter ${attribute.name.toLowerCase()}`}
        />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {label}
      <Input
        className="glass-input h-10 text-sm"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${attribute.name.toLowerCase()}`}
      />
    </div>
  );
}
